import { useState } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { Card, Btn, EmptyState } from "./primitives";

type ReportKey = "legal-team-performance" | "cycle-time-by-agreement-type" | "contracts-by-business-unit" | "spend-by-business-unit";

const REPORTS: { key: ReportKey; title: string; description: string; defaultStart: () => string; defaultEnd: () => string; columns: string[] }[] = [
  {
    key: "legal-team-performance",
    title: "Legal Team Performance",
    description: "Per-lawyer assigned/completed counts, average cycle time, and oldest open request.",
    defaultStart: () => new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10),
    defaultEnd: () => new Date().toISOString().slice(0, 10),
    columns: ["name", "requests_assigned", "requests_completed", "avg_cycle_time_days", "oldest_open_request_age_days"],
  },
  {
    key: "cycle-time-by-agreement-type",
    title: "Cycle Time by Agreement Type",
    description: "Count, average, and median days from request to execution, grouped by contract type.",
    defaultStart: () => new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10),
    defaultEnd: () => new Date().toISOString().slice(0, 10),
    columns: ["agreement_type", "count", "avg_cycle_time_days", "median_cycle_time_days"],
  },
  {
    key: "contracts-by-business-unit",
    title: "Contracts by Business Unit",
    description: "Executed contracts by BU with total estimated deal value and cycle time. Defaults to current quarter.",
    defaultStart: () => { const d = new Date(); return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1).toISOString().slice(0, 10); },
    defaultEnd: () => new Date().toISOString().slice(0, 10),
    columns: ["business_unit", "count_executed", "total_estimated_value", "avg_cycle_time_days"],
  },
  {
    key: "spend-by-business-unit",
    title: "Spend by Business Unit",
    description: "Recorded spend by BU with category breakdown (legal fees, filing fees, settlement, other). Defaults to current quarter.",
    defaultStart: () => { const d = new Date(); return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1).toISOString().slice(0, 10); },
    defaultEnd: () => new Date().toISOString().slice(0, 10),
    columns: ["business_unit", "entry_count", "total_spent", "legal_fees", "filing_fees", "settlement", "other"],
  },
];

const MONEY_COLS = new Set(["total_estimated_value", "total_spent", "legal_fees", "filing_fees", "settlement", "other"]);

const fmtCell = (v: any, col: string) => {
  if (v == null) return "—";
  if (MONEY_COLS.has(col)) return "$" + Number(v).toLocaleString("en-US");
  if (col.includes("cycle_time") || col.includes("age_days")) return Number(v).toLocaleString("en-US");
  return String(v);
};

const labelFor = (col: string) => col.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const InHouseReportCard = ({ report }: { report: typeof REPORTS[number] }) => {
  const [start, setStart] = useState(report.defaultStart());
  const [end, setEnd] = useState(report.defaultEnd());
  const [data, setData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await apiCall(`/reports/in-house/${report.key}?start=${start}&end=${end}`);
      setData(r?.data || []);
    } catch { setData([]); }
    setLoading(false);
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("kontractai_token");
      const res = await fetch(`/api/reports/in-house/${report.key}?start=${start}&end=${end}&format=csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${report.key}_${start}_to_${end}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("CSV download failed"); }
  };

  return (
    <Card style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 2 }}>{report.title}</div>
          <div style={{ fontSize: 11, color: T.txM }}>{report.description}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <label style={{ fontSize: 11, color: T.txD }}>From
          <input type="date" value={start} onChange={e => setStart(e.target.value)} style={{ marginLeft: 6, padding: "4px 8px", fontSize: 12, border: `1px solid ${T.brd}`, borderRadius: 4, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit" }} />
        </label>
        <label style={{ fontSize: 11, color: T.txD }}>To
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={{ marginLeft: 6, padding: "4px 8px", fontSize: 12, border: `1px solid ${T.brd}`, borderRadius: 4, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit" }} />
        </label>
        <Btn v="primary" sz="sm" onClick={() => { run(); }} disabled={loading}>{loading ? "Generating..." : "Generate"}</Btn>
        {data && data.length > 0 && <Btn v="secondary" sz="sm" onClick={() => { downloadCsv(); }}>Download CSV</Btn>}
      </div>
      {data && (
        data.length === 0 ? <EmptyState icon="◈" msg="No data for this range" /> : (
          <div style={{ overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: "left", color: T.txD, fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em" }}>
                  {report.columns.map(c => (
                    <th key={c} style={{ padding: "6px 8px", borderBottom: `1px solid ${T.brd}`, textAlign: c === "name" || c === "agreement_type" || c === "business_unit" ? "left" : "right" }}>{labelFor(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row: any, i: number) => (
                  <tr key={i}>
                    {report.columns.map(c => (
                      <td key={c} style={{ padding: "8px", borderBottom: `1px solid ${T.brd}`, color: T.tx, textAlign: c === "name" || c === "agreement_type" || c === "business_unit" ? "left" : "right", fontVariantNumeric: "tabular-nums" }}>
                        {fmtCell(row[c], c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </Card>
  );
};

export const InHouseReportsSection = () => {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>In-House Reports</div>
      {REPORTS.map(r => <InHouseReportCard key={r.key} report={r} />)}
    </div>
  );
};
